import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react'
import Decimal from 'decimal.js'
import {
  REPORT_LINKS_KEY, formatDuration, type ReportCellLink, type ReportColumnDef,
} from '@project/contracts'
import { Money, StatusMark } from '../design/index.js'
import { Link } from '../app/Link.js'
import { paths } from '../app/router.js'
import { asText, formatDate, formatQuantity, formatRate } from './format.js'
import './report-chassis.css'

/**
 * The grid every report is drawn in (docs/25).
 *
 * Everything here is driven by the column metadata, so a report adopts a
 * behaviour by declaring it:
 *
 * - `link` / `drill`: document numbers and projects open their document; an
 *   amount cell opens the documents behind that figure (RPT-030);
 * - `filter`: the quick search / pick row under the headers, over the loaded
 *   rows, with the entry count and totals recomputed on what is left (RPT-005);
 * - `frozen`: leading identity columns stay put sideways; the header sticks
 *   while scrolling down (RPT-028);
 * - `group`: a second header row over runs of columns (RPT-029);
 * - `total`: summed directly under the column, entries counted in the first
 *   (RPT-031);
 * - every header can be dragged wider or narrower, remembered per report
 *   (RPT-070).
 */

type Row = Record<string, unknown>

const STATUSES = new Set(['DRAFT', 'IN_REVIEW', 'APPROVED', 'CANCELLED'])

/** The tone of a badge cell (RPT-042): its own, or by sign for `signed`. */
export const badgeTone = (badge: ReportColumnDef['badge'], value: unknown): 'good' | 'warn' | 'bad' | null => {
  if (!badge) return null
  if (badge !== 'signed') return badge
  const n = Number(value)
  return !Number.isFinite(n) || n === 0 ? null : n < 0 ? 'bad' : 'good'
}

/** How one value of a column reads. Shared with the summary cards. */
export const reportValue = (
  type: ReportColumnDef['type'], value: unknown, signed = false, badge?: ReportColumnDef['badge'],
): ReactNode => {
  if (value === null || value === undefined || value === '') return null
  const tone = badgeTone(badge, value)
  if (tone) return <span className={`rg-pill rg-pill--${tone}`}>{reportValue(type, value)}</span>
  const s = String(value)
  switch (type) {
    case 'money': {
      const negative = Number(s) < 0
      return (
        <span style={signed ? { color: negative ? 'var(--bad)' : 'var(--good)' } : undefined}>
          <Money value={s} />
        </span>
      )
    }
    case 'qty': return <span className="num">{formatQuantity(s)}</span>
    case 'rate': return <span className="num">{formatRate(s)}</span>
    case 'pct': return Number.isFinite(Number(s)) ? <span className="num">{Number(s).toFixed(2)}%</span> : s
    case 'date': return <span className="num">{formatDate(s)}</span>
    case 'docno': return <span className="mono">{s}</span>
    case 'duration': return <span className="num">{formatDuration(s)}</span>
    case 'status': return STATUSES.has(s) ? <StatusMark status={s as 'DRAFT'} /> : s
    default: return asText(value)
  }
}

/** The text a quick filter matches against: what the cell shows, and the raw value. */
const searchable = (c: ReportColumnDef, value: unknown): string => {
  if (value === null || value === undefined) return ''
  const raw = String(value)
  if (c.type === 'date') return `${raw} ${formatDate(raw)}`.toLowerCase()
  if (c.type === 'duration') return `${raw} ${formatDuration(raw)}`.toLowerCase()
  return raw.toLowerCase()
}

export const filterModeOf = (c: ReportColumnDef): 'text' | 'pick' | null =>
  c.filter === false || c.type === 'details' ? null : c.filter ?? (c.type === 'status' || c.tones ? 'pick' : 'text')

export type ColumnFilters = Readonly<Record<string, string | readonly string[]>>

/** The loaded rows that pass every quick filter. */
export const applyColumnFilters = (
  columns: readonly ReportColumnDef[], rows: readonly Row[], filters: ColumnFilters,
): Row[] => {
  const active = columns
    .map((c) => ({ c, f: filters[c.key] }))
    .filter(({ f }) => (Array.isArray(f) ? f.length > 0 : typeof f === 'string' && f.trim() !== ''))
  if (active.length === 0) return [...rows]
  return rows.filter((row) => active.every(({ c, f }) => {
    const v = row[c.key]
    if (Array.isArray(f)) return f.includes(v === null || v === undefined ? '' : String(v))
    return searchable(c, v).includes(String(f).trim().toLowerCase())
  }))
}

/** Totals over the rows that are left, as decimals. */
export const totalsOver = (columns: readonly ReportColumnDef[], rows: readonly Row[]): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const c of columns) {
    if (!c.total) continue
    let sum = new Decimal(0)
    for (const r of rows) {
      const v = r[c.key]
      if (v === null || v === undefined || v === '') continue
      try { sum = sum.plus(new Decimal(String(v))) } catch { /* a word, not an amount */ }
    }
    out[c.key] = sum.toFixed(c.type === 'qty' ? 3 : c.type === 'duration' ? 0 : 4)
  }
  return out
}

/* Per report, per browser: a width is a preference, like the column choice. */
const readJson = <T,>(store: 'local' | 'session', key: string): T | null => {
  try {
    const raw = (store === 'local' ? window.localStorage : window.sessionStorage).getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}
const writeJson = (store: 'local' | 'session', key: string, value: unknown): void => {
  try {
    const s = store === 'local' ? window.localStorage : window.sessionStorage
    if (value === null) s.removeItem(key)
    else s.setItem(key, JSON.stringify(value))
  } catch { /* the screen still works; the choice just does not persist */ }
}

export interface ReportGridProps {
  readonly columns: readonly ReportColumnDef[]
  readonly rows: readonly Row[]
  /** The server's totals, used while no quick filter is narrowing the rows. */
  readonly totals: Readonly<Record<string, string>>
  readonly companyId: string
  /** Where widths and quick filters are remembered: `report:<key>`. */
  readonly stateKey: string
  readonly onRowClick?: (row: Row) => void
  readonly onCellDrill?: (row: Row, column: ReportColumnDef) => void
  /** The row column whose `alert` / `warning` value tones the whole row. */
  readonly rowTone?: string
}

export const ReportGrid = ({
  columns: declared, rows, totals, companyId, stateKey, onRowClick, onCellDrill, rowTone,
}: ReportGridProps) => {
  // A column nothing in this result fills is left out when it says so: the
  // third workflow step of a two-step workflow (REP1-012).
  const columns = useMemo(() => declared.filter((c) => !c.hideWhenEmpty
    || rows.some((r) => r[c.key] !== undefined && r[c.key] !== null && r[c.key] !== '')), [declared, rows])
  /* Quick filters live for the session, so Back from a document opened in a
     cell lands on the same narrowed list. */
  const filterKey = `report-colfilter:${stateKey}`
  const [filters, setFilters] = useState<ColumnFilters>(() => readJson<ColumnFilters>('session', filterKey) ?? {})
  useEffect(() => { setFilters(readJson<ColumnFilters>('session', filterKey) ?? {}) }, [filterKey])
  const setFilter = (key: string, value: string | readonly string[]): void => {
    setFilters((current) => {
      const next = { ...current, [key]: value }
      writeJson('session', filterKey, next)
      return next
    })
  }

  const widthKey = `colwidths:${stateKey}`
  const [widths, setWidths] = useState<Record<string, number>>(() => readJson('local', widthKey) ?? {})
  useEffect(() => { setWidths(readJson('local', widthKey) ?? {}) }, [widthKey])

  const shown = useMemo(() => applyColumnFilters(columns, rows, filters), [columns, rows, filters])
  const narrowed = shown.length !== rows.length || Object.values(filters).some((f) => (Array.isArray(f) ? f.length : f))
  const footTotals = useMemo(() => (narrowed ? totalsOver(columns, shown) : totals), [narrowed, columns, shown, totals])

  /* Frozen columns: the leading run of `frozen` ones, each offset by the
     measured widths of those before it. */
  const frozenCount = useMemo(() => {
    let n = 0
    while (n < columns.length && columns[n]?.frozen) n += 1
    return n
  }, [columns])
  const headRefs = useRef<Array<HTMLTableCellElement | null>>([])
  const [offsets, setOffsets] = useState<number[]>([])
  useLayoutEffect(() => {
    if (frozenCount === 0) { setOffsets([]); return }
    const next: number[] = []
    let left = 0
    for (let i = 0; i < frozenCount; i += 1) {
      next.push(left)
      const c = columns[i]
      left += headRefs.current[i]?.offsetWidth || (c ? widths[c.key] ?? c.width ?? 200 : 200)
    }
    setOffsets((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next))
  }, [frozenCount, columns, widths, shown.length])

  const frozenStyle = (i: number): CSSProperties | undefined =>
    i < frozenCount ? { left: offsets[i] ?? 0 } : undefined
  const frozenClass = (i: number): string =>
    i < frozenCount ? ` rg-frozen${i === frozenCount - 1 ? ' rg-frozen--edge' : ''}` : ''

  /* Drag to resize (RPT-070). */
  const [resizing, setResizing] = useState<string | null>(null)
  const startResize = useCallback((e: ReactPointerEvent, key: string, start: number) => {
    e.preventDefault()
    e.stopPropagation()
    const x0 = e.clientX
    setResizing(key)
    let latest = start
    const move = (ev: PointerEvent): void => {
      latest = Math.max(60, Math.round(start + ev.clientX - x0))
      setWidths((w) => ({ ...w, [key]: latest }))
    }
    const up = (): void => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      setResizing(null)
      setWidths((w) => {
        const next = { ...w, [key]: latest }
        writeJson('local', widthKey, next)
        return next
      })
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [widthKey])
  const resetWidth = (key: string): void => {
    setWidths((w) => {
      const { [key]: _gone, ...rest } = w
      writeJson('local', widthKey, Object.keys(rest).length ? rest : null)
      return rest
    })
  }

  const grouped = columns.some((c) => c.group)
  const filterable = columns.some((c) => filterModeOf(c) !== null)
  const right = (c: ReportColumnDef): boolean =>
    c.align ? c.align === 'right' : c.type === 'money' || c.type === 'qty' || c.type === 'rate' || c.type === 'pct'
  const widthOf = (c: ReportColumnDef): CSSProperties => {
    const w = widths[c.key] ?? c.width
    return w === undefined ? { minWidth: 200 } : { width: w, minWidth: w, maxWidth: widths[c.key] ? w : undefined }
  }

  /* Header rows: a group row over runs of the same `group`, columns without
     one spanning both rows. Each row sticks under the one above it. */
  const HEAD_H = 44
  const groupRuns = useMemo(() => {
    const runs: Array<{ label: string; span: number; start: number }> = []
    columns.forEach((c, i) => {
      const last = runs[runs.length - 1]
      if (c.group && last && last.label === c.group && last.start + last.span === i) last.span += 1
      else if (c.group) runs.push({ label: c.group, span: 1, start: i })
    })
    return runs
  }, [columns])

  const header = (c: ReportColumnDef, i: number, rowSpan?: number, top = 0) => (
    <th
      key={c.key}
      ref={(el) => { headRefs.current[i] = el }}
      scope="col"
      rowSpan={rowSpan}
      className={`rg-th${right(c) ? ' is-right' : ''}${c.group ? ' rg-th--grouped' : ''}${frozenClass(i)}`}
      style={{ ...widthOf(c), top, ...frozenStyle(i) }}
    >
      <span className="rg-th__inner">
        {c.label}
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${c.label}`}
          title="Drag to resize, double-click to reset"
          className={`rg-resize${resizing === c.key ? ' rg-resize--on' : ''}`}
          onPointerDown={(e) => startResize(e, c.key, headRefs.current[i]?.offsetWidth ?? widths[c.key] ?? c.width ?? 200)}
          onDoubleClick={() => resetWidth(c.key)}
        />
      </span>
    </th>
  )

  const pickValues = (c: ReportColumnDef): string[] =>
    [...new Set(rows.map((r) => (r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key]))))].sort()

  const filterTop = grouped ? HEAD_H * 2 : HEAD_H

  const body = (row: Row, c: ReportColumnDef): ReactNode => {
    // The DETAILS button on every row (RPT-017): the row's own drill.
    if (c.type === 'details') {
      return onRowClick ? (
        <button type="button" className="btn btn--ghost rg-details"
          onClick={(e) => { e.stopPropagation(); onRowClick(row) }}>
          {c.label}
        </button>
      ) : null
    }
    // A coloured pill whose tone the value picks (INT-003).
    const tone = c.tones ? c.tones[String(row[c.key] ?? '')] : undefined
    if (c.tones && row[c.key] !== null && row[c.key] !== undefined && row[c.key] !== '') {
      return <span className={`rg-pill rg-pill--${tone ?? 'muted'}`}>{asText(row[c.key])}</span>
    }
    const value = reportValue(c.type, row[c.key], c.signed, c.badge)
    if (value === null) return null
    const link = (row[REPORT_LINKS_KEY] as Record<string, ReportCellLink> | undefined)?.[c.key]
    if (link) {
      return (
        <Link to={paths.document(companyId, link.route, link.id)} className="rg-link"
          onClick={(e) => e.stopPropagation()}>
          {value}
        </Link>
      )
    }
    if (c.drill && onCellDrill) {
      return (
        <button type="button" className="linkish rg-drill" title={c.drill.label ?? `What makes up ${c.label.toLowerCase()}`}
          onClick={(e) => { e.stopPropagation(); onCellDrill(row, c) }}>
          {value}
        </button>
      )
    }
    return value
  }

  return (
    <div className="tbl-wrap rg-wrap">
      <table className="tbl rg">
        <thead>
          {grouped ? (
            <tr>
              {columns.map((c, i) => {
                if (!c.group) return header(c, i, 2, 0)
                const run = groupRuns.find((r) => r.start === i)
                if (!run) return null
                return (
                  <th key={`g:${i}`} scope="colgroup" colSpan={run.span}
                    className={`rg-th rg-th--group rg-th--grouped${frozenClass(i)}`} style={{ top: 0, ...frozenStyle(i) }}>
                    {run.label}
                  </th>
                )
              })}
            </tr>
          ) : null}
          <tr>
            {columns.map((c, i) => (grouped ? (c.group ? header(c, i, undefined, HEAD_H) : null) : header(c, i)))}
          </tr>
          {filterable ? (
            <tr>
              {columns.map((c, i) => {
                const mode = filterModeOf(c)
                const value = filters[c.key]
                return (
                  <th key={`f:${c.key}`} className={`rg-th rg-filter${frozenClass(i)}`} style={{ top: filterTop, ...frozenStyle(i) }}>
                    {mode === 'text' ? (
                      <input
                        className="rg-filter__input"
                        type="search"
                        aria-label={`Search ${c.label}`}
                        placeholder="Search"
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => setFilter(c.key, e.currentTarget.value)}
                      />
                    ) : mode === 'pick' ? (
                      <PickFilter
                        label={c.label}
                        values={pickValues(c)}
                        chosen={Array.isArray(value) ? value : []}
                        render={(v) => (v === '' ? '(blank)' : c.type === 'status' && STATUSES.has(v) ? v.replace('_', ' ').toLowerCase() : v)}
                        onChange={(next) => setFilter(c.key, next)}
                      />
                    ) : null}
                  </th>
                )
              })}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr><td className="tbl__none" colSpan={Math.max(columns.length, 1)}>Nothing matches the column filters.</td></tr>
          ) : shown.map((row, n) => (
            <tr
              key={n}
              className={[
                onRowClick ? 'tbl__row--click' : '',
                rowTone && row[rowTone] === 'alert' ? 'rg-row--alert' : rowTone && row[rowTone] === 'warning' ? 'rg-row--warn' : '',
              ].filter(Boolean).join(' ') || undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={onRowClick ? (e) => {
                if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onRowClick(row) }
              } : undefined}
            >
              {columns.map((c, i) => (
                <td key={c.key} className={`${right(c) ? 'is-right' : ''}${frozenClass(i)}`} style={frozenStyle(i)} data-label={c.label}>
                  {c.oncePer && n > 0 && shown[n - 1]?.[c.oncePer] === row[c.oncePer] ? null : body(row, c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            {columns.map((c, i) => (
              <td key={c.key} className={`${right(c) ? 'is-right' : ''}${frozenClass(i)}`} style={frozenStyle(i)}>
                {i === 0
                  ? <span>Total entries: <span className="num">{shown.length}</span>{c.total ? <> · {reportValue(c.type, footTotals[c.key] ?? '0')}</> : null}</span>
                  : c.total ? reportValue(c.type, footTotals[c.key] ?? '0', c.signed, c.badge) : null}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** A multi-select of the values present in a column. */
const PickFilter = ({ label, values, chosen, render, onChange }: {
  label: string
  values: readonly string[]
  chosen: readonly string[]
  render: (v: string) => string
  onChange: (next: readonly string[]) => void
}) => (
  <details className="rg-pick">
    <summary className="rg-filter__input" aria-label={`Filter ${label}`}>
      <span>{chosen.length === 0 ? 'All' : chosen.length === 1 ? render(chosen[0]!) : `${chosen.length} chosen`}</span>
      <span aria-hidden="true">▾</span>
    </summary>
    <div className="rg-pick__menu">
      {values.map((v) => (
        <label key={v} className="rg-pick__opt">
          <input type="checkbox" checked={chosen.includes(v)}
            onChange={() => onChange(chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v])} />
          {render(v)}
        </label>
      ))}
      {chosen.length > 0 ? (
        <button type="button" className="linkish" style={{ marginTop: 6, padding: '4px 6px' }} onClick={() => onChange([])}>Show all</button>
      ) : null}
    </div>
  </details>
)
